import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { profileApi } from '../lib/api';
import { getPrivateKey } from '../lib/crypto';
import { enterSharedAccount, leaveSharedAccount } from '../lib/sharedAccess';
import { takeInvitationSecret, hasInvitationSecret } from '../lib/invitationSecret';
import { Button } from '../components/ui/button';
import { Loader2, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';

const TYPE_LABELS = {
  solicitor: 'Solicitor',
  legal_advocate: 'Legal advocate',
  support_worker: 'Support worker',
  counsellor: 'Counsellor',
  therapist: 'Therapist',
  gp_doctor: 'GP / doctor',
  family_member: 'Family member',
  friend: 'Friend',
  other: 'Other',
};

const PERM_LABELS = { read: 'Read only', read_write: 'Read and write' };


/**
 * Shown post-login when the user has at least one incoming invitation. Lets
 * them choose between their own account (if they have one — see
 * has_own_data below) and any shared accounts, and accept/decline anything
 * still pending.
 */
const AccountChooser = () => {
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // invitation id currently being acted on
  const [activatingOwn, setActivatingOwn] = useState(false);

  const loadInvitations = useCallback(() => {
    return profileApi
      .incomingInvitations()
      .then((r) => setInvitations(r.data?.invitations || []))
      .catch(() => setInvitations([]));
  }, []);

  useEffect(() => {
    // Always start in the user's own account context (clears any stale state).
    leaveSharedAccount();
    loadInvitations().finally(() => setLoading(false));
  }, [loadInvitations]);

  const active = invitations.filter((i) => i.status === 'active');
  const awaitingDecision = invitations.filter((i) => i.status === 'pending' || i.status === 'awaiting_acceptance');

  // If the user has their own account and nothing waiting on them, skip the
  // chooser entirely.
  useEffect(() => {
    if (!loading && user?.has_own_data && invitations.length === 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user?.has_own_data, invitations.length, navigate]);

  const handleOwn = () => {
    leaveSharedAccount();
    navigate('/dashboard', { replace: true });
  };

  const handleAddOwnAccount = async () => {
    setActivatingOwn(true);
    try {
      await profileApi.activateOwnAccount();
      await loadUser();
      navigate('/onboarding', { replace: true });
    } catch {
      toast.error('Could not start your own account. Try again in a moment.');
      setActivatingOwn(false);
    }
  };

  const enterAccount = async (inv) => {
    const ownPrivateKey = getPrivateKey();
    if (!ownPrivateKey) {
      toast.error('Your encryption key is not available. Sign in again.');
      navigate('/login');
      return;
    }
    setBusyId(inv.id);
    try {
      const { data } = await profileApi.accessSharedAccount(inv.id, {
        invitee_private_key_pem: ownPrivateKey,
      });
      enterSharedAccount({
        ownerId: data.owner.id,
        ownerName: data.owner.full_name || data.owner.email,
        permission: data.invitation.permission,
        invitationId: inv.id,
        ownerPrivateKeyPem: data.owner_private_key_pem,
        ownPrivateKeyPem: ownPrivateKey,
      });
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'We could not open this account.');
      setBusyId(null);
    }
  };

  const handleAccept = async (inv) => {
    setBusyId(inv.id);
    try {
      const secret = inv.needs_secret ? takeInvitationSecret(inv.id) : null;
      if (inv.needs_secret && !secret) {
        toast.error('Use the link from your invite email to accept this — it needs a one-time code we only send there.');
        setBusyId(null);
        return;
      }
      await profileApi.acceptInvitation(inv.id, secret);
      toast.success('Invitation accepted');
      // Straight into the account rather than making them click twice.
      await enterAccount({ ...inv, status: 'active' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept this invitation.');
      setBusyId(null);
    }
  };

  const handleDecline = async (inv) => {
    setBusyId(inv.id);
    try {
      await profileApi.declineInvitation(inv.id);
      toast.success('Invitation declined');
      await loadInvitations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not decline this invitation.');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDialog = useConfirm();
  const handleLeave = async (inv, ev) => {
    ev.stopPropagation();
    if (!(await confirmDialog({
      title: 'Remove your own access?',
      detail: `${inv.owner_full_name || inv.owner_email}'s account`,
      description: 'You will no longer be able to see their records. They can invite you again.',
      confirmLabel: 'Remove my access',
      destructive: true,
    }))) return;
    setBusyId(inv.id);
    try {
      await profileApi.leaveSharedAccount(inv.id);
      toast.success('Access removed');
      await loadInvitations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove access.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-brand" />
      </div>
    );
  }

  const rowClass =
    'w-full text-left bg-ground hover:bg-inset transition-colors duration-state rounded-lg p-5 flex items-center gap-4 min-h-[44px]';

  return (
    <div className="min-h-screen bg-ground flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-2xl mt-6 sm:mt-12" data-testid="account-chooser">
        <div className="mb-6 flex items-center gap-3">
          <PdMark size={28} className="text-brand" />
          <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
        </div>

        <div className="bg-surface rounded-xl p-7 sm:p-9 space-y-8">
          <header className="space-y-3">
            <h1 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
              Which account do you want to open, {user?.full_name?.split(' ')[0] || user?.email}?
            </h1>
            <p className="text-ink-70">
              Other people have given you access to their records. You can switch accounts
              at any time from the banner at the top of the page.
            </p>
          </header>

          {user?.has_own_data && (
            <section className="space-y-2">
              <p className="pd-eyebrow">Your own account</p>
              <button type="button" className={rowClass} onClick={handleOwn} data-testid="chooser-own-account">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink">{user?.full_name || 'Your own account'}</p>
                  <p className="text-sm text-ink-55 truncate">{user?.email}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-ink-55 stroke-[1.5]" aria-hidden="true" />
              </button>
            </section>
          )}

          {active.length > 0 && (
            <section className="space-y-2">
              <p className="pd-eyebrow">Accounts shared with you</p>
              {active.map((inv) => (
                <div key={inv.id} className={rowClass}>
                  <button
                    type="button"
                    className="flex-1 min-w-0 flex items-center gap-4 text-left"
                    onClick={() => enterAccount(inv)}
                    data-testid={`chooser-shared-${inv.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink truncate flex items-center gap-2">
                        {inv.owner_full_name || inv.owner_email}
                        {inv.has_new_activity && (
                          <span
                            aria-label="New activity"
                            className="w-2 h-2 rounded-full shrink-0 bg-brand"
                          />
                        )}
                      </p>
                      <p className="text-sm text-ink-55 mt-0.5">
                        You as {TYPE_LABELS[inv.invitee_type] || inv.invitee_type} · {PERM_LABELS[inv.permission] || inv.permission}
                      </p>
                    </div>
                    {busyId === inv.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-brand shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-ink-55 stroke-[1.5] shrink-0" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleLeave(inv, e)}
                    className="text-sm text-ink-55 hover:text-breach-ink px-2 py-1 rounded-md hover:bg-surface shrink-0"
                    aria-label={`Remove your access to ${inv.owner_full_name || inv.owner_email}'s account`}
                  >
                    Remove access
                  </button>
                </div>
              ))}
            </section>
          )}

          {awaitingDecision.length > 0 && (
            <section className="space-y-2">
              <p className="pd-eyebrow">Waiting for your decision</p>
              {awaitingDecision.map((inv) => (
                <div key={inv.id} className="bg-ground rounded-lg p-5" data-testid={`chooser-pending-${inv.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink truncate">
                        {inv.owner_full_name || inv.owner_email}
                      </p>
                      <p className="text-sm text-ink-55 mt-0.5">
                        As {TYPE_LABELS[inv.invitee_type] || inv.invitee_type} · {PERM_LABELS[inv.permission] || inv.permission}
                      </p>
                      {inv.needs_secret && (
                        <p className="text-sm text-amber-ink mt-1">
                          Use the link from your invite email to accept this.
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === inv.id}
                      onClick={() => handleDecline(inv)}
                      data-testid={`chooser-decline-${inv.id}`}
                    >
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      disabled={busyId === inv.id || (inv.needs_secret && !hasInvitationSecret(inv.id))}
                      onClick={() => handleAccept(inv)}
                      data-testid={`chooser-accept-${inv.id}`}
                    >
                      {busyId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept'}
                    </Button>
                  </div>
                </div>
              ))}
            </section>
          )}

          {!user?.has_own_data && (
            <section className="space-y-2">
              <button
                type="button"
                className="w-full text-left border border-dashed border-shape hover:border-brand hover:bg-inset transition-colors duration-state rounded-lg p-5 flex items-center gap-3 min-h-[44px]"
                onClick={handleAddOwnAccount}
                disabled={activatingOwn}
                data-testid="chooser-add-own-account"
              >
                {activatingOwn ? (
                  <Loader2 className="w-5 h-5 animate-spin text-brand" />
                ) : (
                  <Plus className="w-5 h-5 text-ink-55" aria-hidden="true" />
                )}
                <span className="font-semibold text-ink">Add your own account</span>
              </button>
            </section>
          )}

          <p className="rounded-lg bg-amber-wash text-amber-ink p-4 text-[15px]">
            When you open someone else&apos;s account you are acting for them. A banner at the top
            will remind you whose records you are reading. Treat what you see with care.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountChooser;
